import type { Ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { shallowRef } from 'vue';
import { useScopedLocalStorage } from '#layers/base/composables/use-scoped-local-storage';
import { storageKey } from '#layers/base/utils/storage-key';

export interface FeatureFlagOptions {
	default: boolean;
	title: string;
	description: string;
}

export interface FeatureFlagMeta extends FeatureFlagOptions {
	id: string;
	storageKey: string;
}

const FEATURE_FLAG_META = Symbol('feature-flag-meta');

export const FEATURE_FLAG_STORAGE_PREFIX = storageKey('feature-flag:');

export interface FeatureFlag extends Ref<boolean, boolean> {
	readonly [FEATURE_FLAG_META]: FeatureFlagMeta;
}

const featureFlags = shallowRef<FeatureFlag[]>([]);

function getFeatureFlagStorageKey(id: string) {
	return `${FEATURE_FLAG_STORAGE_PREFIX}${id}`;
}

function createFeatureFlag(meta: FeatureFlagMeta): FeatureFlag {
	const featureFlag = useLocalStorage(meta.storageKey, meta.default) as FeatureFlag;

	Object.defineProperty(featureFlag, FEATURE_FLAG_META, {
		value: meta,
		enumerable: false,
		configurable: true,
		writable: false,
	});

	return featureFlag;
}

export function defineFeatureFlag(id: string, options: FeatureFlagOptions): FeatureFlag {
	const featureFlag = createFeatureFlag({
		id,
		storageKey: getFeatureFlagStorageKey(id),
		...options,
	});

	featureFlags.value = [...featureFlags.value.filter((flag) => getFeatureFlagMeta(flag).id !== id), featureFlag];

	return featureFlag;
}

export function getFeatureFlagMeta(featureFlag: FeatureFlag) {
	return featureFlag[FEATURE_FLAG_META];
}

export function useRegisteredFeatureFlags() {
	return featureFlags;
}

export function resetFeatureFlagsToDefaults() {
	for (const featureFlag of featureFlags.value) {
		featureFlag.value = getFeatureFlagMeta(featureFlag).default;
	}
}

export const featureFlagsVisible = useScopedLocalStorage('feature-flags-visible', false);

export const SHUFFLE_ITERATION_COUNT = 20;
export const SHUFFLE_MIN_DELAY_MS = 100;
export const SHUFFLE_MAX_DELAY_MS = 450;

export function resetFeatureFlagsForTesting() {
	featureFlags.value = [];
	featureFlagsVisible.value = false;

	if (import.meta.client === false) {
		return;
	}

	// Match on the full feature-flag prefix, never on `STORAGE_PREFIX` alone: widening it here
	// would delete every other Studio key (auth, login-providers cache, UI preferences).
	for (const key of Object.keys(localStorage)) {
		if (key.startsWith(FEATURE_FLAG_STORAGE_PREFIX)) {
			localStorage.removeItem(key);
		}
	}
}
